import { AvvisoQuoteCondomino } from '@/components/AvvisoQuoteCondomino';
import { AssistenteCondomino } from '@/components/AssistenteCondomino';
import { InformativaPrivacyCondomino } from '@/components/InformativaPrivacyCondomino';
import { useState, useEffect, useCallback } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Fingerprint,
  KeyRound,
  Users,
  Truck,
  Receipt,
  Bell,
  Calendar,
  PieChart,
  FileText,
  FolderOpen,
  Settings,
  Menu,
  X,
  Building2,
  Clock,
  Calculator,
  LogOut,
  ChevronDown,
  ChevronRight,
  Table2,
  Split,
  Scale,
  Wallet,
  Landmark,
  Link2,
  FileArchive,
  Database,
  UserPlus,
  Crown,
  Shield,
  AlertTriangle,
  XCircle,
  ArrowRight,
  Mail,
  Send,
  Activity,
  Palette,
  ClipboardList,
  User,
  Briefcase,
  Euro,
  CreditCard,
  Banknote,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import CookieBanner from '@/components/CookieBanner';
import NotificationBell from '@/components/NotificationBell';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Voci del menu che si accendono quando ci sono novita' non lette: percorso della pagina -> tipi di notifica dell'app collegati.
// Aprendo la pagina le notifiche di quei tipi risultano lette e la voce torna normale.
const TIPI_NOVITA = {
  '/communications': ['COMUNICAZIONE'],
  '/bacheca-condomini': ['BACHECA_MESSAGGIO'],
  '/dichiarazioni-pagamento': ['DICHIARAZIONE_PAGAMENTO'],
  '/dashboard': ['DICHIARAZIONE_ESITO'],
};

const menuItems = [
  // 1. Dashboard
  { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
  // Gestione degli accessi dei condomini (utenti, password, lettera d'invito): in primo piano per l'amministratore, non nascosta in Impostazioni
  { icon: Users, label: 'Accessi condomini', path: '/accessi-condomini', description: 'Utenti, password, lettera', adminOnly: true },
  { icon: Fingerprint, label: 'Registro accessi', path: '/registro-accessi', description: 'Chi entra, quando, da quale IP', adminOnly: true },
  // Grafici per anno e criticita' riscontrate nella ricostruzione
  // contabile. Pagina separata dalla Dashboard di proposito.
  { icon: Activity, label: 'Cruscotto', path: '/cruscotto',
    description: 'Grafici e criticita per anno', adminOnly: true },
  // Registro presenze, traccia di verbale e relazione: contengono nomi e
  // millesimi, quindi la rotta che li serve li da' al solo amministratore.
  { icon: ClipboardList, label: 'Assemblea', path: '/assemblea',
    description: 'Registro presenze, verbale, relazione', adminOnly: true },
  // Sportello della portineria: incassi allo sportello. Lo vede il portiere;
  // l'amministratore lo apre per controllare e confermare.
  { icon: Banknote, label: 'Portineria', path: '/portineria',
    description: 'Incassi allo sportello', portineriaOnly: true },
  // Cambio password per tutti i ruoli (prima il condomino non aveva nessuna schermata)
  { icon: KeyRound, label: 'Cambia password', path: '/change-password' },

  // 2. Anagrafica (sottomenu)
  { 
    icon: Users, 
    label: 'Anagrafica', 
    description: 'Gestione anagrafica',
    isSubmenu: true,
    basePath: '/registry',
    submenuItems: [
      { icon: Users, label: 'Unità e Proprietari', path: '/registry', description: 'Registro unità', adminOnly: true },
      { icon: UserPlus, label: 'Inviti', path: '/invites', description: 'Invita residenti', adminOnly: true },
      { icon: Truck, label: 'Fornitori', path: '/suppliers', description: 'Anagrafica fornitori', adminOnly: true },
    ]
  },
  
  // 3. Contabilità (sottomenu)
  { 
    icon: Receipt, 
    label: 'Contabilità', 
    description: 'Gestione contabile',
    isSubmenu: true,
    basePath: '/accounting',
    submenuItems: [
      { icon: Receipt, label: 'Spese/Fatture', path: '/expenses', description: 'Fatture e allocazioni', adminOnly: true },
      { icon: Table2, label: 'Millesimi', path: '/millesimi', description: 'Tabelle millesimali', adminOnly: true },
      { icon: Split, label: 'Ripartizioni', path: '/allocations', description: 'Ripartizione spese', adminOnly: true },
      { icon: Euro, label: 'Quote Mensili', path: '/monthly-quotes', description: 'Rate condominiali' },
      { icon: Scale, label: 'Situazione al subentro', path: '/opening-balances', description: 'Crediti e debiti al passaggio di consegne', adminOnly: true },
      { icon: PieChart, label: 'Bilanci', path: '/budgets', description: 'Preventivi e consuntivi', adminOnly: true },
      { icon: Calculator, label: 'Ritenute', path: '/tax', description: 'Modulo 770', adminOnly: true },
      { icon: Wallet, label: 'Prima nota', path: '/prima-nota', description: 'Cassa contanti', adminOnly: true },
      { icon: Euro, label: 'Pagamenti dichiarati', path: '/dichiarazioni-pagamento', description: 'Da verificare', adminOnly: true },
      { icon: Receipt, label: 'Numerazione ricevute', path: '/ricevute-numerazione', description: 'Progressivo per anno e data', adminOnly: true },
    ]
  },
  
  // 4. Banca (sottomenu) - Solo admin
  { 
    icon: Landmark, 
    label: 'Banca', 
    description: 'Operazioni bancarie',
    isSubmenu: true,
    basePath: '/bank',
    adminOnly: true,
    submenuItems: [
      { icon: Landmark, label: 'Import Estratti', path: '/bank-import', description: 'Carica estratti conto' },
      { icon: Link2, label: 'Riconciliazione', path: '/reconciliation', description: 'Abbina movimenti' },
    ]
  },
  
  // 5. Personale (sottomenu) - Solo admin
  { 
    icon: Briefcase, 
    label: 'Personale', 
    description: 'Gestione dipendenti',
    isSubmenu: true,
    basePath: '/staff',
    adminOnly: true,
    submenuItems: [
      { icon: Users, label: 'Gestione Staff', path: '/staff', description: 'Lista dipendenti', adminOnly: true },
      { icon: Briefcase, label: 'Buste Paga & F24', path: '/staff/consultant', description: 'Documenti HR', consultantOnly: true },
      { icon: User, label: 'Il Mio Portale', path: '/staff/my', description: 'Area dipendente', employeeOnly: true },
    ]
  },
  
  // 6. Comunicazioni (sottomenu)
  { 
    icon: Bell, 
    label: 'Comunicazioni', 
    description: 'Gestione comunicazioni',
    isSubmenu: true,
    basePath: '/communications',
    submenuItems: [
      { icon: Bell, label: 'Bacheca', path: '/communications', description: 'Avvisi condominiali' },
      { icon: Mail, label: 'Centro Comunicazioni', path: '/communications-center', description: 'Gestione e protocollo', adminOnly: true },
      { icon: Mail, label: 'Le Mie Comunicazioni', path: '/my-communications', description: 'Messaggi ricevuti', residentOnly: true },
      { icon: Users, label: 'Bacheca condomini', path: '/bacheca-condomini', description: 'Messaggi dei condomini' },
      { icon: AlertTriangle, label: 'Segnalazioni', path: '/incidents', description: 'Guasti e sinistri' },
      { icon: Calendar, label: 'Assemblee', path: '/meetings', description: 'Convocazioni e verbali' },
    ]
  },
  
  // 7. Documenti (sottomenu) - Semplificato per residenti
  { 
    icon: FolderOpen, 
    label: 'Documenti', 
    description: 'Archivio documenti',
    isSubmenu: true,
    basePath: '/documents',
    submenuItems: [
      { icon: FolderOpen, label: 'Archivio', path: '/documents', description: 'Registro documenti' },
      { icon: FileText, label: 'Report', path: '/reports', description: 'Reportistica', adminOnly: true },
      { icon: FileArchive, label: 'Export', path: '/exports', description: 'Esportazione dati', adminOnly: true },
      { icon: Database, label: 'Backup', path: '/backups', description: 'Backup e ripristino', adminOnly: true },
    ]
  },
  
  // 8. Impostazioni (sottomenu) - Solo admin
  { 
    icon: Settings, 
    label: 'Impostazioni', 
    description: 'Configurazione',
    isSubmenu: true,
    basePath: '/settings',
    adminOnly: true,
    submenuItems: [
      { icon: Settings, label: 'Generali', path: '/settings', description: 'Impostazioni base' },
      { icon: CreditCard, label: 'Pagamenti', path: '/settings/payments', description: 'Gateway e bonifici' },
      { icon: Send, label: 'Notifiche', path: '/settings/notifications', description: 'Template e invii' },
      { icon: Palette, label: 'Branding', path: '/settings/branding', description: 'Logo e intestazione' },
      { icon: Shield, label: 'GDPR', path: '/privacy/registro-trattamenti', description: 'Registro trattamenti' },
      { icon: Clock, label: 'Retention', path: '/settings/retention', description: 'Conservazione dati' },
      { icon: ClipboardList, label: 'Audit Log', path: '/audit-log', description: 'Registro attività' },
    ]
  },
  
  // 9. Workspace (sottomenu) - Solo admin
  { 
    icon: Crown, 
    label: 'Workspace', 
    description: 'Gestione abbonamento',
    isSubmenu: true,
    basePath: '/workspace',
    adminOnly: true,
    submenuItems: [
      { icon: Crown, label: 'Piano', path: '/workspace', description: 'Abbonamento attivo' },
      { icon: Wallet, label: 'Fatturazione', path: '/workspace/billing', description: 'Utilizzo e pagamenti' },
    ]
  },
];

// Super Admin menu items (shown only for super admins)
const superAdminMenuItem = { icon: Shield, label: 'Super Admin', path: '/superadmin', description: 'Gestione piattaforma' };
const healthMenuItem = { icon: Activity, label: 'Health Check', path: '/admin/health', description: 'Stato sistema' };
const schedulerMenuItem = { icon: Clock, label: 'Job Scheduler', path: '/admin/scheduler', description: 'Notifiche automatiche' };

// Numero pulsante accanto a una voce del menu con novita' non lette
const PallinoNovita = ({ n }) => (n > 0 ? (
  <span className="ml-auto inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full bg-orange-500 px-2 text-xs font-bold text-white animate-pulse"
        title={`${n} da leggere`}>{n}</span>
) : null);

export const AppShell =  () => {
  const { user, token, selectedCondominium, condominiums, selectCondominium, logout, accessoComeCondomino, tornaAdmin } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [workspaceStatus, setWorkspaceStatus] = useState(null);
  const [openSubmenus, setOpenSubmenus] = useState({});

  // Novita' non lette per tipo (stesse notifiche della campanella)
  const [novita, setNovita] = useState({});
  const caricaNovita = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/notifications/unread-by-type`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setNovita((await res.json()).per_tipo || {});
    } catch (e) { /* il menu resta com'e' */ }
  }, [token]);
  useEffect(() => {
    caricaNovita();
    const giro = setInterval(caricaNovita, 60000);
    return () => clearInterval(giro);
  }, [caricaNovita]);
  const nuoviPercorso = (percorso) => (TIPI_NOVITA[percorso] || []).reduce((t, tipo) => t + (novita[tipo] || 0), 0);
  const nuoviVoce = (voce, ruolo) => (voce.isSubmenu && voce.submenuItems
    ? voce.submenuItems.filter((sub) => shouldShowSubmenuItem(sub, ruolo)).reduce((t, sub) => t + nuoviPercorso(sub.path), 0)
    : nuoviPercorso(voce.path));
  const navigate = useNavigate();
  const location = useLocation();

  // Aprendo la pagina, le notifiche collegate si segnano lette
  useEffect(() => {
    const tipi = TIPI_NOVITA[location.pathname];
    if (!tipi || !token || !tipi.some((t) => novita[t] > 0)) return;
    fetch(`${API_URL}/api/notifications/mark-read-by-type`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ types: tipi }),
    }).then(caricaNovita).catch(() => {});
  }, [location.pathname, novita, token, caricaNovita]);

  // Fetch workspace status for banners
  const fetchWorkspaceStatus = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/workspace/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setWorkspaceStatus(data);
        
        // Redirect to status page if SUSPENDED (except super admin)
        if (data.is_suspended && !user?.is_super_admin) {
          navigate('/workspace/status');
        }
      }
    } catch (error) {
      console.error('Error fetching workspace status:', error);
    }
  }, [token, user, navigate]);

  useEffect(() => {
    fetchWorkspaceStatus();
  }, [fetchWorkspaceStatus]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;
  
  const toggleSubmenu = (label) => {
    setOpenSubmenus(prev => ({ ...prev, [label]: !prev[label] }));
  };
  
  const shouldShowSubmenuItem = (subItem, role) => {
    // Admin-only items visible only to ADMIN and SUPER_ADMIN
    if (subItem.adminOnly && role !== 'ADMIN' && role !== 'SUPER_ADMIN' && role !== 'ACCOUNTANT') return false;
    // Employee-only items
    if (subItem.employeeOnly && role !== 'EMPLOYEE') return false;
    // Portineria: portiere, amministratore e commercialista
    if (subItem.portineriaOnly && !['EMPLOYEE', 'ADMIN', 'SUPER_ADMIN', 'ACCOUNTANT'].includes(role)) return false;
    // Consultant-only items visible to consultants and admins
    if (subItem.consultantOnly && role !== 'LABOR_CONSULTANT' && role !== 'ADMIN') return false;
    // Resident-only items visible only to residents
    if (subItem.residentOnly && role !== 'RESIDENT') return false;
    return true;
  };

  // Check if user is admin/accountant (can edit data)
  const isUserAdmin = () => {
    const role = selectedCondominium?.role;
    return role === 'ADMIN' || role === 'SUPER_ADMIN' || role === 'ACCOUNTANT';
  };

  const handleNavigation = (path) => {
    // Block navigation for suspended accounts (except status page)
    if (workspaceStatus?.is_suspended && path !== '/workspace/status' && !user?.is_super_admin) {
      toast.error('Account sospeso. Contatta il supporto.');
      navigate('/workspace/status');
      return;
    }
    navigate(path);
    setSidebarOpen(false);
  };

  // Format date for banner
  const formatBannerDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long'
    });
  };

  // Check if banner should be shown
  // Lo stato dell'abbonamento dello studio riguarda solo chi amministra: condomini, personale e consulenti non lo vedono
  const vedeStatoAbbonamento = () => ['ADMIN', 'ACCOUNTANT'].includes(selectedCondominium?.role);

  const shouldShowBanner = () => {
    if (!vedeStatoAbbonamento()) return false;
    if (!workspaceStatus?.has_workspace || user?.is_super_admin) return false;
    const { plan_status, has_usage_warning, has_exceeded_limit } = workspaceStatus;
    return plan_status !== 'ACTIVE' || has_usage_warning || has_exceeded_limit;
  };

  // Render status banner
  const renderStatusBanner = () => {
    if (!vedeStatoAbbonamento()) return null;
    if (!workspaceStatus?.has_workspace || user?.is_super_admin) return null;

    const { plan_status, days_remaining, trial_ends_at, lock_reason, has_usage_warning, has_exceeded_limit, usage_warnings } = workspaceStatus;

    // Priority: SUSPENDED > EXPIRED > EXCEEDED LIMIT > TRIAL > USAGE WARNING

    if (plan_status === 'SUSPENDED') {
      return (
        <div className="bg-gray-800 text-white px-4 py-2 flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            <span>Account sospeso{lock_reason ? `: ${lock_reason}` : ''}. Contatta il supporto.</span>
          </div>
          <Button 
            size="sm" 
            variant="secondary" 
            className="h-7 text-xs"
            onClick={() => navigate('/workspace/status')}
          >
            Vedi Stato
            <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </div>
      );
    }

    if (plan_status === 'EXPIRED') {
      return (
        <div className="bg-red-600 text-white px-4 py-2 flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <XCircle className="h-4 w-4" />
            <span>Trial scaduta: modalità sola lettura. Non puoi effettuare modifiche.</span>
          </div>
          <Button 
            size="sm" 
            variant="secondary" 
            className="h-7 text-xs"
            onClick={() => navigate('/workspace/status')}
          >
            Attiva Abbonamento
            <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </div>
      );
    }

    // Usage limit exceeded (hard block)
    if (has_exceeded_limit && usage_warnings?.length > 0) {
      const exceededWarning = usage_warnings.find(w => w.severity === 'exceeded');
      return (
        <div className="bg-red-600 text-white px-4 py-2 flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <XCircle className="h-4 w-4" />
            <span>⚠️ {exceededWarning?.message || 'Limite risorse raggiunto'}. Effettua l&apos;upgrade per continuare.</span>
          </div>
          <Button 
            size="sm" 
            variant="secondary" 
            className="h-7 text-xs"
            onClick={() => navigate('/workspace/billing')}
          >
            Gestisci Piano
            <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </div>
      );
    }

    if (plan_status === 'TRIAL') {
      const isUrgent = days_remaining !== null && days_remaining <= 3;
      return (
        <div className={`${isUrgent ? 'bg-red-600' : 'bg-yellow-500'} text-white px-4 py-2 flex items-center justify-between text-sm`}>
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            <span>
              {isUrgent 
                ? `⚠️ Versione demo: scade ${days_remaining === 0 ? 'oggi' : `tra ${days_remaining} giorni`}!`
                : `Versione demo: scade il ${formatBannerDate(trial_ends_at)}`
              }
            </span>
          </div>
          <Button 
            size="sm" 
            variant="secondary" 
            className="h-7 text-xs"
            onClick={() => navigate('/workspace/status')}
          >
            Passa ad Abbonamento
            <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </div>
      );
    }

    // Usage warnings (80%+ but not exceeded)
    if (has_usage_warning && usage_warnings?.length > 0 && plan_status === 'ACTIVE') {
      const warningCount = usage_warnings.length;
      const firstWarning = usage_warnings[0];
      return (
        <div className="bg-orange-500 text-white px-4 py-2 flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            <span>
              {warningCount === 1 
                ? `⚠️ ${firstWarning.message}`
                : `⚠️ ${warningCount} risorse vicine al limite (es. ${firstWarning.label}: ${firstWarning.percent}%)`
              }
            </span>
          </div>
          <Button 
            size="sm" 
            variant="secondary" 
            className="h-7 text-xs"
            onClick={() => navigate('/workspace/billing')}
          >
            Vedi Utilizzo
            <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Global Status Banner */}
      {renderStatusBanner()}

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-stone-200 shadow-sm" style={{ top: shouldShowBanner() ? '40px' : '0' }}>
        <div className="flex items-center justify-between px-4 h-16">
          <div className="flex items-center gap-3">
            <img
              src="/assets/artego-icon.svg"
              alt="Artego"
              className="h-8 w-8"
              data-testid="mobile-logo"
            />
            <div className="flex flex-col">
              <span className="font-heading font-bold text-base text-slate-900 leading-tight">ARTEGO.APP</span>
              <span className="text-xs text-stone-500 leading-tight">Amministro Condomini</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="mobile-menu-toggle"
            >
              {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </header>

      {/* Desktop Sidebar */}
      <aside 
        className="hidden lg:fixed lg:flex lg:w-64 lg:flex-col bg-white border-r border-stone-200 shadow-sm"
        style={{ 
          top: shouldShowBanner() ? '40px' : '0',
          height: shouldShowBanner() ? 'calc(100vh - 40px)' : '100vh'
        }}
      >
        <div className="flex flex-col flex-1 min-h-0">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-stone-200">
            <div className="flex items-center gap-3">
              <img
                src="/assets/artego-icon.svg"
                alt="Artego"
                className="h-9 w-9 flex-shrink-0"
                data-testid="desktop-logo"
              />
              <div className="min-w-0">
                <h1 className="font-heading font-bold text-lg text-slate-900 leading-tight">ARTEGO.APP</h1>
                <p className="text-xs text-stone-500 leading-tight">Amministro Condomini</p>
              </div>
            </div>
            <NotificationBell />
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {/* Super Admin Items (only for super admins) */}
            {user?.is_super_admin && (
              <>
                <button
                  onClick={() => handleNavigation(superAdminMenuItem.path)}
                  data-testid="nav-superadmin"
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-md font-medium transition-all ${
                    isActive(superAdminMenuItem.path)
                      ? 'bg-purple-600 text-white shadow-sm'
                      : 'text-purple-700 bg-purple-50 hover:bg-purple-100'
                  }`}
                >
                  <Shield className="h-5 w-5 flex-shrink-0" strokeWidth={1.5} />
                  <div className="flex-1 text-left">
                    <div className="text-sm">{superAdminMenuItem.label}</div>
                    <div className="text-xs opacity-75 mt-0.5">{superAdminMenuItem.description}</div>
                  </div>
                </button>
                <button
                  onClick={() => handleNavigation(healthMenuItem.path)}
                  data-testid="nav-health"
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-md font-medium transition-all mb-2 ${
                    isActive(healthMenuItem.path)
                      ? 'bg-green-600 text-white shadow-sm'
                      : 'text-green-700 bg-green-50 hover:bg-green-100'
                  }`}
                >
                  <Activity className="h-5 w-5 flex-shrink-0" strokeWidth={1.5} />
                  <div className="flex-1 text-left">
                    <div className="text-sm">{healthMenuItem.label}</div>
                    <div className="text-xs opacity-75 mt-0.5">{healthMenuItem.description}</div>
                  </div>
                </button>
                <button
                  onClick={() => handleNavigation(schedulerMenuItem.path)}
                  data-testid="nav-scheduler"
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-md font-medium transition-all mb-2 ${
                    isActive(schedulerMenuItem.path)
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-blue-700 bg-blue-50 hover:bg-blue-100'
                  }`}
                >
                  <Clock className="h-5 w-5 flex-shrink-0" strokeWidth={1.5} />
                  <div className="flex-1 text-left">
                    <div className="text-sm">{schedulerMenuItem.label}</div>
                    <div className="text-xs opacity-75 mt-0.5">{schedulerMenuItem.description}</div>
                  </div>
                </button>
              </>
            )}
            
            {menuItems
              .filter(item => {
                const role = selectedCondominium?.role;
                // Filter adminOnly items for non-admin roles
                // Coerente con shouldShowSubmenuItem: ADMIN, SUPER_ADMIN e ACCOUNTANT
                // possono vedere le voci/sottomenu amministrativi (il backend autorizza
                // ACCOUNTANT su banca, contabilita, export, ecc.).
                if (item.adminOnly && role !== 'ADMIN' && role !== 'SUPER_ADMIN' && role !== 'ACCOUNTANT') {
                  return false;
                }
                // Filter residentOnly items for non-resident roles
                if (item.residentOnly && role !== 'RESIDENT') {
                  return false;
                }
                // Filter employeeOnly items
                if (item.portineriaOnly && !['EMPLOYEE', 'ADMIN', 'SUPER_ADMIN', 'ACCOUNTANT'].includes(role)) {
                  return false;
                }
                if (item.employeeOnly && role !== 'EMPLOYEE') {
                  return false;
                }
                // consultantOnly visible to LABOR_CONSULTANT and ADMIN
                if (item.consultantOnly && role !== 'LABOR_CONSULTANT' && role !== 'ADMIN') {
                  return false;
                }
                // For submenu items, check if any subitems are visible
                if (item.isSubmenu && item.submenuItems) {
                  const hasVisibleSubitems = item.submenuItems.some(sub => shouldShowSubmenuItem(sub, role));
                  return hasVisibleSubitems;
                }
                return true;
              })
              .map((item) => {
              const Icon = item.icon;
              
              // Handle submenu items
              if (item.isSubmenu && item.submenuItems) {
                const role = selectedCondominium?.role;
                const visibleSubitems = item.submenuItems.filter(sub => shouldShowSubmenuItem(sub, role));
                
                // Check if submenu should be open based on current path
                const nuoviVoceN = nuoviVoce(item, role);
                const isSubmenuOpen = openSubmenus[item.label] || nuoviVoceN > 0 ||
                  (item.basePath && location.pathname.startsWith(item.basePath)) ||
                  visibleSubitems.some(sub => location.pathname === sub.path || location.pathname.startsWith(sub.path + '/'));
                const isAnySubitemActive = visibleSubitems.some(sub => isActive(sub.path) || location.pathname.startsWith(sub.path + '/'));
                
                return (
                  <div key={item.label}>
                    <button
                      onClick={() => toggleSubmenu(item.label)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-md font-medium transition-all ${
                        nuoviVoceN > 0
                          ? 'bg-amber-100 text-amber-900 ring-2 ring-amber-400 font-bold'
                          : isAnySubitemActive
                            ? 'bg-primary/10 text-primary'
                            : 'text-slate-700 hover:bg-stone-100 hover:text-slate-900'
                      }`}
                    >
                      <Icon className="h-5 w-5 flex-shrink-0" strokeWidth={1.5} />
                      <div className="flex-1 text-left">
                        <div className="text-sm">{item.label}</div>
                        {nuoviVoceN > 0
                          ? <div className="text-xs mt-0.5">{nuoviVoceN === 1 ? '1 novità da leggere' : `${nuoviVoceN} novità da leggere`}</div>
                          : item.description && (
                            <div className="text-xs opacity-75 mt-0.5">{item.description}</div>
                          )}
                      </div>
                      <PallinoNovita n={nuoviVoceN} />
                      {isSubmenuOpen ? (
                        <ChevronDown className="h-4 w-4 flex-shrink-0" />
                      ) : (
                        <ChevronRight className="h-4 w-4 flex-shrink-0" />
                      )}
                    </button>
                    {isSubmenuOpen && (
                      <div className="ml-4 mt-1 space-y-1 border-l-2 border-stone-200 pl-2">
                        {visibleSubitems.map((subItem) => {
                          const SubIcon = subItem.icon;
                          const subActive = isActive(subItem.path);
                          const subNuovi = nuoviPercorso(subItem.path);
                          return (
                            <button
                              key={subItem.path}
                              onClick={() => handleNavigation(subItem.path)}
                              className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-all ${
                                subNuovi > 0
                                  ? 'bg-amber-100 text-amber-900 ring-2 ring-amber-400 font-bold'
                                  : subActive
                                    ? 'bg-primary text-primary-foreground shadow-sm'
                                    : 'text-slate-600 hover:bg-stone-100 hover:text-slate-900'
                              }`}
                            >
                              <SubIcon className="h-4 w-4 flex-shrink-0" strokeWidth={1.5} />
                              <div className="flex-1 text-left">
                                <div>{subItem.label}</div>
                              </div>
                              <PallinoNovita n={subNuovi} />
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              
              // Regular menu item
              // For communications-center, append condominiumId
              let path = item.path;
              if (item.path === '/communications-center' && selectedCondominium?.condominium?.id) {
                path = `/communications-center/${selectedCondominium.condominium.id}`;
              }
              const active = isActive(item.path) || (item.path === '/communications-center' && location.pathname.startsWith('/communications-center'));
              const voceNuovi = nuoviPercorso(item.path);
              return (
                <button
                  key={item.path}
                  onClick={() => handleNavigation(path)}
                  data-testid={`nav-${(item.path || item.basePath || 'menu').substring(1)}`}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-md font-medium transition-all ${
                    voceNuovi > 0
                      ? 'bg-amber-100 text-amber-900 ring-2 ring-amber-400 font-bold'
                      : active
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-slate-700 hover:bg-stone-100 hover:text-slate-900'
                  }`}
                >
                  <Icon className="h-5 w-5 flex-shrink-0" strokeWidth={1.5} />
                  <div className="flex-1 text-left">
                    <div className="text-sm">{item.label}</div>
                    {item.description && (
                      <div className="text-xs opacity-75 mt-0.5">{item.description}</div>
                    )}
                  </div>
                  <PallinoNovita n={voceNuovi} />
                </button>
              );
            })}
          </nav>

          {/* User Section */}
          <div className="px-3 py-4 border-t border-stone-200">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-full flex items-center gap-3 px-4 py-3 hover:bg-stone-100 rounded-md transition-colors" data-testid="user-menu-trigger">
                  <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                    <Building2 className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="text-sm font-medium text-slate-900 truncate">
                      {selectedCondominium?.condominium.name || 'Seleziona condominio'}
                    </div>
                    <div className="text-xs text-stone-600">
                      {selectedCondominium?.role || 'Nessun ruolo'}
                    </div>
                  </div>
                  <ChevronDown className="h-4 w-4 text-stone-400 flex-shrink-0" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end">
                <DropdownMenuLabel>Il mio Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {condominiums.length > 0 && (
                  <>
                    <DropdownMenuLabel className="text-xs font-normal text-stone-600">
                      Condomini
                    </DropdownMenuLabel>
                    {condominiums.map((condo) => (
                      <DropdownMenuItem
                        key={condo.condominium.id}
                        onClick={() => selectCondominium(condo)}
                        className={selectedCondominium?.condominium.id === condo.condominium.id ? 'bg-stone-100' : ''}
                        data-testid={`select-condo-${condo.condominium.id}`}
                      >
                        <Building2 className="h-4 w-4 mr-2" />
                        <div>
                          <div className="text-sm font-medium">{condo.condominium.name}</div>
                          <div className="text-xs text-stone-600">{condo.role}</div>
                        </div>
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-button">
                  <LogOut className="h-4 w-4 mr-2" />
                  Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Footer with Artego SRLS info */}
          <div className="px-4 py-3 border-t border-stone-200 bg-stone-50">
            <div className="flex items-center gap-3">
              <img
                src="/assets/artego-symbol.png"
                alt="Artego SRLS"
                className="h-12 w-auto"
              />
              <div>
                <p className="font-semibold text-stone-700 text-sm">Artego SRLS</p>
                <p className="text-xs text-stone-400">© 2026</p>
              </div>
            </div>
            <a href="/legale.html" target="_blank" rel="noopener" className="mt-2 block text-[11px] text-stone-500 hover:text-stone-800 transition-colors" data-testid="sidebar-legal-link">Note legali</a>
          </div>
        </div>
      </aside>

      {/* Mobile Drawer */}
      {sidebarOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 bg-black/50 z-40"
            onClick={() => setSidebarOpen(false)}
            data-testid="mobile-drawer-overlay"
          />
          <aside className="lg:hidden fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-xl transform transition-transform duration-200 ease-in-out">
            <div className="flex flex-col h-full">
              {/* Mobile drawer header */}
              <div className="flex items-center justify-between h-16 px-4 border-b border-stone-200">
                <div className="flex items-center gap-3 min-w-0">
                  <img
                    src="/assets/artego-icon.svg"
                    alt="Artego"
                    className="h-8 w-8 flex-shrink-0"
                  />
                  <div className="min-w-0">
                    <span className="font-heading font-bold text-base text-slate-900 block">ARTEGO.APP</span>
                    <span className="text-xs text-stone-500 block">Amministro Condomini</span>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSidebarOpen(false)}
                  data-testid="close-drawer"
                  className="flex-shrink-0"
                >
                  <X className="h-6 w-6" />
                </Button>
              </div>

              {/* Navigation */}
              <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
                {menuItems
                  .filter(item => {
                    // Stesso gating per ruolo della sidebar desktop: evita di mostrare
                    // ai non aventi diritto (es. RESIDENT) le voci amministrative.
                    const role = selectedCondominium?.role;
                    if (item.adminOnly && role !== 'ADMIN' && role !== 'SUPER_ADMIN' && role !== 'ACCOUNTANT') return false;
                    if (item.residentOnly && role !== 'RESIDENT') return false;
                    if (item.employeeOnly && role !== 'EMPLOYEE') return false;
                    if (item.consultantOnly && role !== 'LABOR_CONSULTANT' && role !== 'ADMIN') return false;
                    if (item.isSubmenu && item.submenuItems) {
                      return item.submenuItems.some(sub => shouldShowSubmenuItem(sub, role));
                    }
                    return true;
                  })
                  .map((item) => {
                  const Icon = item.icon;
                  // Per i sottomenu su mobile: punta alla prima sottovoce visibile
                  // (alcuni basePath, es. /bank, non sono rotte reali e darebbero 404).
                  const role = selectedCondominium?.role;
                  const mobileTarget = item.path
                    || (item.isSubmenu && item.submenuItems
                        ? (item.submenuItems.find(sub => shouldShowSubmenuItem(sub, role))?.path || item.basePath)
                        : item.basePath);
                  const active = isActive(mobileTarget);
                  const nuoviMobile = nuoviVoce(item, role);
                  return (
                    <button
                      key={item.path || item.basePath || item.label}
                      onClick={() => handleNavigation(mobileTarget)}
                      data-testid={`mobile-nav-${(mobileTarget || 'menu').substring(1)}`}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-md font-medium transition-all ${
                        nuoviMobile > 0
                          ? 'bg-amber-100 text-amber-900 ring-2 ring-amber-400 font-bold'
                          : active
                            ? 'bg-primary text-primary-foreground shadow-sm'
                            : 'text-slate-700 hover:bg-stone-100 hover:text-slate-900'
                      }`}
                    >
                      <Icon className="h-5 w-5 flex-shrink-0" strokeWidth={1.5} />
                      <div className="flex-1 text-left">
                        <div className="text-sm">{item.label}</div>
                        {item.description && (
                          <div className="text-xs opacity-75 mt-0.5">{item.description}</div>
                        )}
                      </div>
                      <PallinoNovita n={nuoviMobile} />
                    </button>
                  );
                })}
              </nav>

              {/* Mobile Footer with Artego SRLS info */}
              <div className="px-4 py-3 border-t border-stone-200 bg-stone-50">
                <div className="flex items-center gap-3">
                  <img
                    src="/assets/artego-symbol.png"
                    alt="Artego SRLS"
                    className="h-10 w-auto"
                  />
                  <div>
                    <p className="font-semibold text-stone-700 text-sm">Artego SRLS</p>
                    <p className="text-xs text-stone-400">© 2026</p>
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </>
      )}

      <InformativaPrivacyCondomino />
      <AvvisoQuoteCondomino />

      {accessoComeCondomino && (
        <div className="fixed bottom-0 inset-x-0 z-[100] bg-amber-500 text-black px-4 py-2 flex flex-wrap items-center justify-between gap-2 text-sm shadow-lg">
          <span>Stai guardando l'app come <strong>{user?.full_name || user?.email}</strong> (accesso dell'amministratore, registrato nel log).</span>
          <button type="button" onClick={tornaAdmin} className="rounded bg-black text-white px-3 py-1 font-medium">Torna all'amministratore</button>
        </div>
      )}

      {/* Main Content */}
      <main 
        className="lg:pl-64 flex flex-col min-h-screen" 
        style={{ 
          paddingTop: shouldShowBanner()
            ? '104px' // 40px banner + 64px header
            : '64px'  // 64px header only
        }}
      >
        <div className="flex-1 p-6 md:p-8 lg:p-12 lg:pt-6">
          <Outlet />
          <AssistenteCondomino />
        </div>
        
        {/* Global Footer */}
        <footer className="border-t border-stone-200 bg-stone-50 py-6 px-6 md:px-8 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <img
                src="/assets/artego-symbol.png"
                alt="Artego SRLS"
                className="h-10 w-auto"
              />
              <div className="text-sm text-stone-600">
                <p className="font-semibold text-stone-800">Artego SRLS</p>
                <p>Via M.se di Villabianca, 114 - 90143 Palermo</p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8 text-sm text-stone-600">
              <div className="flex flex-col">
                <span className="text-xs text-stone-500 uppercase tracking-wide">Contatti</span>
                <a href="tel:+393520498295" className="hover:text-slate-900">+39 3520498295</a>
              </div>
              <div className="flex flex-col">
                <span className="text-xs text-stone-500 uppercase tracking-wide">Email</span>
                <a href="mailto:info@artegosrls.it" className="hover:text-slate-900">info@artegosrls.it</a>
                <a href="mailto:artegosrls@pec.it" className="hover:text-slate-900">artegosrls@pec.it</a>
              </div>
              <div className="flex flex-col">
                <span className="text-xs text-stone-500 uppercase tracking-wide">P.IVA</span>
                <span>07221580827</span>
              </div>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-stone-200 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs text-stone-500">
            <Link to="/informativa-condomini" className="underline hover:text-slate-900">Informativa privacy per i condomini</Link>
            <div className="flex items-center gap-2">
              <p>© 2026 Artego SRLS. Tutti i diritti riservati.</p>
              <span className="hidden md:inline">•</span>
              <a href="https://www.artegosrls.it" target="_blank" rel="noopener noreferrer" className="hover:text-slate-900 font-medium">
                www.artegosrls.it
              </a>
            </div>
            <div className="flex items-center gap-4">
              <a href="https://www.facebook.com/artegosrls" target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-blue-600 transition-colors" aria-label="Facebook">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="https://www.instagram.com/artegosrls" target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-pink-600 transition-colors" aria-label="Instagram">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              </a>
              <a href="https://wa.me/393520498295" target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-green-600 transition-colors" aria-label="WhatsApp">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              </a>
              <a href="https://www.youtube.com/@artegosrls" target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-red-600 transition-colors" aria-label="YouTube">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              </a>
            </div>
          </div>
        </footer>
      </main>
      
      {/* GDPR Cookie Consent Banner */}
      <CookieBanner />
    </div>
  );
};